import { useEffect, useCallback, useState, useRef } from "react";
import { useSocket } from "./useSocket";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface OpsNotification {
  type:
    | "NEW_DISPUTE"
    | "MESSAGE_FLAGGED"
    | "TRIAL_J3"
    | "PENDING_ARTISAN"
    | "REFUND_CLAIM"
    | "TENDER_INTERESTED"
    | "AO_DOSSIER_CONFIE"
    // Lot A (revue #336) : alerte anti-usurpation — 2FA activée par un freelance.
    | "FREELANCE_2FA_ENROLLED";
  timestamp: string;
  disputeId?: string;
  projectId?: string;
  severity?: string;
  matchId?: string;
  messageId?: string;
  riskScore?: number;
  companyName?: string;
  companyId?: string;
  expiresAt?: string;
  artisanProfileId?: string;
  tenderId?: string;
  tenderTitle?: string;
  name?: string;
  reference?: string;
  reason?: string;
  link?: string;
}

const STORAGE_KEY = "ops_notifications";
const MAX_STORED = 100;
const DEDUP_WINDOW_MS = 30_000;

const TOAST_CONFIG: Record<string, { style: string; duration: number; title: string }> = {
  NEW_DISPUTE: { style: "destructive", duration: 15_000, title: "Nouveau litige" },
  MESSAGE_FLAGGED: { style: "warning", duration: 8_000, title: "Message suspect" },
  TRIAL_J3: { style: "info", duration: 8_000, title: "Trial expire bientôt" },
  PENDING_ARTISAN: { style: "default", duration: 8_000, title: "Artisan en attente" },
  REFUND_CLAIM: { style: "warning", duration: 8_000, title: "Demande de remboursement" },
  // Un client veut candidater : un dossier est à préparer dans la GO Queue.
  TENDER_INTERESTED: { style: "info", duration: 12_000, title: "Client intéressé par un AO — dossier à préparer" },
  // Un client a confié son dossier à Constrigo : 490 € HT à facturer (tous plans).
  AO_DOSSIER_CONFIE: { style: "warning", duration: 15_000, title: "Dossier AO confié — à facturer (490 € HT)" },
};

function getStoredNotifications(): (OpsNotification & { id: string; read: boolean })[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function storeNotifications(items: (OpsNotification & { id: string; read: boolean })[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_STORED)));
  } catch {
    // Storage full or unavailable (private mode) — notifications stay in-memory only.
  }
}

function makeId(n: OpsNotification): string {
  return `${n.type}_${n.disputeId || n.messageId || n.companyId || n.matchId || n.artisanProfileId || n.tenderId || ""}_${n.timestamp}`;
}

export function useOpsNotifications() {
  const { socket, connected } = useSocket();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(getStoredNotifications);
  const dedupRef = useRef<Map<string, number>>(new Map());

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotification = useCallback(
    (payload: OpsNotification) => {
      // Clé construite avec TOUS les identifiants : deux dossiers AO d'une même
      // entreprise, ou deux messages signalés sur un même projet, restent
      // deux notifications distinctes.
      const dedupKey = [
        payload.type,
        payload.disputeId,
        payload.messageId,
        payload.matchId,
        payload.projectId,
        payload.companyId,
      ]
        .map((x) => x ?? "")
        .join("_");
      const now = Date.now();
      const lastSeen = dedupRef.current.get(dedupKey);
      if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return;
      dedupRef.current.set(dedupKey, now);

      const id = makeId(payload);
      const item = { ...payload, id, read: false };

      setNotifications((prev) => {
        const updated = [item, ...prev].slice(0, MAX_STORED);
        storeNotifications(updated);
        return updated;
      });

      const cfg = TOAST_CONFIG[payload.type] || TOAST_CONFIG.PENDING_ARTISAN;
      const description =
        (payload.type === "TENDER_INTERESTED" || payload.type === "AO_DOSSIER_CONFIE") &&
        payload.companyName &&
        payload.tenderTitle
          ? `${payload.companyName} — « ${payload.tenderTitle.slice(0, 80)} »`
          : payload.companyName || payload.name || payload.reference || payload.reason || "";

      if (payload.type === "NEW_DISPUTE" && payload.severity === "critical") {
        toast.error(cfg.title, {
          description,
          duration: Infinity,
          action: payload.link
            ? { label: "Voir", onClick: () => navigate(payload.link!) }
            : undefined,
        });
      } else if (cfg.style === "destructive") {
        toast.error(cfg.title, {
          description,
          duration: cfg.duration,
          action: payload.link
            ? { label: "Voir", onClick: () => navigate(payload.link!) }
            : undefined,
        });
      } else if (cfg.style === "warning") {
        toast.warning(cfg.title, {
          description,
          duration: cfg.duration,
          action: payload.link
            ? { label: "Voir", onClick: () => navigate(payload.link!) }
            : undefined,
        });
      } else if (cfg.style === "info") {
        toast.info(cfg.title, {
          description,
          duration: cfg.duration,
          action: payload.link
            ? { label: "Voir", onClick: () => navigate(payload.link!) }
            : undefined,
        });
      } else {
        toast(cfg.title, {
          description,
          duration: cfg.duration,
          action: payload.link
            ? { label: "Voir", onClick: () => navigate(payload.link!) }
            : undefined,
        });
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!socket || !connected) return;

    socket.on("ops:notification", handleNotification);
    return () => {
      socket.off("ops:notification", handleNotification);
    };
  }, [socket, connected, handleNotification]);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, read: true }));
      storeNotifications(updated);
      return updated;
    });
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    storeNotifications([]);
  }, []);

  return { notifications, unreadCount, markAllRead, clearAll };
}
